import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { images } from '../../components/shared/assets';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { ContentfulImage, ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import { usePageContent, usePackages, useSiteSettings } from '../../hooks/useContentful';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../components/shared/FilterBar';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import './OffersPage.css';

const OffersPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDestination, setSelectedDestination] = useState<string | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('offers');
  const { data: packagesData } = usePackages();
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const allOffers = packagesData || [];
  const siteSettings = siteSettingsData?.[0]?.fields as any;

  // Filter offers based on selected filters
  const filteredOffers = allOffers.filter(offer => {
    const offerFields = offer.fields as any;
    const tags = offerFields.tags || [];

    // Filter by destination
    if (selectedDestination) {
      const hasDestination = tags.some((tag: string) =>
        tag.toLowerCase().includes(selectedDestination.toLowerCase())
      );
      if (!hasDestination) return false;
    }

    // Filter by type
    if (selectedType) {
      const hasType = tags.some((tag: string) =>
        tag.toLowerCase().includes(selectedType.toLowerCase()) ||
        offerFields.category?.toLowerCase().includes(selectedType.toLowerCase())
      );
      if (!hasType) return false;
    }

    return true;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const offers = filteredOffers.slice(startIndex, endIndex);

  const handleViewMore = (offerId: string) => {
    navigate(`/offers/${offerId}/`);
  };

  return (
    <div className="offers-page">
      {/* Navigation */}
      <SmartNavigation
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
      />

      {/* Hero Section */}
      <section className="offers-hero">
        <div className="offers-hero-image" style={{ backgroundImage: `url('${images.bajaPhoto}')` }}>
          <div className="offers-hero-content">
            <h1>
              <ContentfulText
                content={pageContent?.heroTitle}
                fallback="The Perfect South American Adventure"
              />
            </h1>
            <p>
              <ContentfulMultilineText
                content={pageContent?.heroDescription}
                fallback="Every journey with Awasi immerses you in the rich stories, landscapes, and traditions of each destination. Craft unforgettable, personal, and authentic experiences with the help of our local experts, ready to design your ideal itinerary, recommend exclusive offers, and combine our unique lodges into a truly once-in-a-lifetime adventure."
              />
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="offers-filters">
        <FilterBar
          className="offers-filter-bar"
          dropdowns={[
            {
              id: 'destination',
              type: 'destination',
              level: 'brand',
              options: DESTINATION_OPTIONS,
              selectedValue: selectedDestination,
              onSelect: (value: string) => setSelectedDestination(value === selectedDestination ? undefined : value)
            },
            {
              id: 'type',
              type: 'type',
              level: 'brand',
              options: TYPE_OPTIONS,
              selectedValue: selectedType,
              onSelect: (value: string) => setSelectedType(value === selectedType ? undefined : value)
            }
          ]}
          alignment="center"
        />
      </section>

      {/* Offers Grid */}
      <section className="offers-grid">
        <div className="offers-container">
          {offers.length > 0 ? (
            offers.map((offer) => {
              const offerFields = offer.fields as any;
              const tags = offerFields.tags || [];
              const imageUrl = offerFields.featuredImage?.fields?.file?.url;

              return (
                <div key={offer.sys.id} className="offer-card">
                  <div className="offer-image">
                    <img src={imageUrl ? `https:${imageUrl}` : images.galleryMain} alt={offerFields.title} />
                    <div className="offer-tags">
                      {tags.map((tag: string, index: number) => (
                        <span key={index} className="tag">
                          <ContentfulText
                            content={tag}
                            fallback={tag}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="offer-content">
                    <h3>
                      <ContentfulText
                        content={offerFields.title}
                        fallback="Offer Title"
                      />
                    </h3>
                    <div className="offer-links">
                      <CTALink
                        text={offerFields.buttonText || pageContent?.defaultButtonText || "VIEW MORE"}
                        onClick={() => handleViewMore(offerFields.slug || offer.sys.id)}
                        type="primary"
                        color="awasi"
                      />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            // Fallback static offers
            <>
              <div className="offer-card">
                <div className="offer-image">
                    <img src="/assets/images/offers-stay-longer.jpg" alt="Stay Longer"/>
                  <div className="offer-tags">
                    <span className="tag">ATACAMA / IGUAZÚ / SANTA CATARINA</span>
                  </div>
                </div>
                <div className="offer-content">
                  <h3>Stay Longer</h3>
                  <div className="offer-links">
                    <CTALink
                      text="VIEW MORE"
                      onClick={() => handleViewMore('stay-longer')}
                      type="primary"
                      color="awasi"
                    />
                  </div>
                </div>
              </div>

              <div className="offer-card">
                <div className="offer-image">
                  <ContentfulImage imageKey="gallery-wildlife" fallbackSrc={images.galleryWildlife} alt="Adventure Offer" />
                  <div className="offer-tags">
                    <span className="tag">ATACAMA / IGUAZÚ / PATAGONIA</span>
                  </div>
                </div>
                <div className="offer-content">
                  <h3>Combine Atacama, Patagonia & Iguazú</h3>
                  <div className="offer-links">
                    <CTALink
                      text="VIEW MORE"
                      onClick={() => handleViewMore('three-lodges')}
                      type="primary"
                      color="awasi"
                    />
                  </div>
                </div>
              </div>

              <div className="offer-card">
                <div className="offer-image">
                  <img src={images.galleryVilla} alt="Adventure Offer" />
                  <div className="offer-tags">
                    <span className="tag">ATACAMA / PATAGONIA</span>
                  </div>
                </div>
                <div className="offer-content">
                  <h3>Combine Atacama & Patagonia</h3>
                  <div className="offer-links">
                    <CTALink
                      text="VIEW MORE"
                      onClick={() => handleViewMore('atacama-patagonia')}
                      type="primary"
                      color="awasi"
                    />
                  </div>
                </div>
              </div>

              <div className="offer-card">
                <div className="offer-image">
                  <img src={images.villaInterior} alt="Wellness Offer" />
                  <div className="offer-tags">
                    <span className="tag">ATACAMA</span>
                  </div>
                </div>
                <div className="offer-content">
                  <h3>RE-ENERGIZE wellness immersion</h3>
                  <div className="offer-links">
                    <CTALink
                      text="VIEW MORE"
                      onClick={() => handleViewMore('wellness-immersion')}
                      type="primary"
                      color="awasi"
                    />
                  </div>
                </div>
              </div>

              <div className="offer-card">
                <div className="offer-image">
                  <img src={images.lodgeExterior} alt="Adventure Offer" />
                  <div className="offer-tags">
                    <span className="tag">PATAGONIA / ATACAMA</span>
                  </div>
                </div>
                <div className="offer-content">
                  <h3>Combine Patagonia & Atacama</h3>
                  <div className="offer-links">
                    <CTALink
                      text="VIEW MORE"
                      onClick={() => handleViewMore('patagonia-atacama')}
                      type="primary"
                      color="awasi"
                    />
                  </div>
                </div>
              </div>

              <div className="offer-card">
                <div className="offer-image">
                  <img src={images.restaurantBanner} alt="Iguazú Offer" />
                  <div className="offer-tags">
                    <span className="tag">IGUAZÚ</span>
                  </div>
                </div>
                <div className="offer-content">
                  <h3>Iguazú Experience</h3>
                  <div className="offer-links">
                    <CTALink
                      text="VIEW MORE"
                      onClick={() => handleViewMore('iguazu-experience')}
                      type="primary"
                      color="awasi"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="offers-pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <HandDrawnButton
                key={pageNum}
                text={pageNum.toString()}
                onClick={() => setCurrentPage(pageNum)}
                variant="brand"
                color="awasi"
                animation={currentPage === pageNum ? "stroke-to-fill" : "stroke-to-stroke"}
                size="normal"
                className={`offers-page-button ${currentPage === pageNum ? 'offers-page-active' : ''}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Instagram Section */}
      <InstagramSection
        posts={[
          { src: images.instagram1, alt: "Instagram post 1" },
          { src: images.instagram2, alt: "Instagram post 2" },
          { src: images.instagram3, alt: "Instagram post 3" },
          { src: images.instagram4, alt: "Instagram post 4" }
        ]}
      />

      {/* Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default OffersPage;